import { Badge, type BadgeVariant } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";
import type { InventoryReason } from "../../api/inventory";

/**
 * How an inventory decision's reason reads, shared by the feed previews and run
 * lines (inventory updates page) and the product's Inventory rules tab.
 */

/** "blocked by 258088" / "limited by 258088" for a bundle line; nothing for anything else. */
export function BundleItemNote({ reason, detail }: { reason: InventoryReason; detail?: string | null }) {
  const { t } = useTranslation();
  if (!detail) return null;
  return (
    <span className="text-[12px] text-ink-muted">
      {reason === "ITEM_BLOCKED"
        ? t("inv-update.line.blocked-by", "blocked by {{sku}}", { sku: detail })
        : t("inv-update.line.limited-by", "limited by {{sku}}", { sku: detail })}
    </span>
  );
}

export function ReasonBadge({ reason }: { reason: InventoryReason }) {
  const { t } = useTranslation();
  return (
    <Badge variant={reasonVariant(reason)} size="sm">
      {reasonLabel(t, reason)}
    </Badge>
  );
}

export function reasonVariant(reason: InventoryReason): BadgeVariant {
  switch (reason) {
    case "DISPATCHABLE":
      return "success";
    case "FLOORED":
      return "info";
    case "OUT_OF_STOCK":
      return "warning";
    case "UNKNOWN_SKU":
      return "error";
    case "DELISTED":
      return "neutral";
    default:
      return "neutral";
  }
}

export function reasonLabel(t: (key: string, fallback: string) => string, reason: InventoryReason): string {
  switch (reason) {
    case "UNKNOWN_SKU":
      return t("inv-update.reason.unknown-sku", "Unknown SKU");
    case "DELISTED":
      return t("inv-update.reason.delisted", "Delisted");
    case "DISCONTINUED":
      return t("inv-update.reason.discontinued", "Discontinued");
    case "NOT_LAUNCHED":
      return t("inv-update.reason.not-launched", "Not launched");
    case "NOT_SOLD_IN_MARKET":
      return t("inv-update.reason.not-sold-in-market", "Not sold in market");
    case "NOT_LISTED":
      return t("inv-update.reason.not-listed", "Not listed");
    case "NO_RULE":
      return t("inv-update.reason.no-rule", "No rule");
    case "BLOCKED":
      return t("inv-update.reason.blocked", "Blocked");
    case "ITEM_BLOCKED":
      return t("inv-update.reason.item-blocked", "Item blocked");
    case "OUT_OF_STOCK":
      return t("inv-update.reason.out-of-stock", "Out of stock");
    case "FLOORED":
      return t("inv-update.reason.floored", "Floored");
    case "DISPATCHABLE":
      return t("inv-update.reason.dispatchable", "Dispatchable");
  }
}

